// "local" was removed from the runtime profile vocabulary on 2026-05-19
// after the `.env.local` / `.env.sandbox` split was found to be ceremony
// only (both pointed at the same Supabase project in practice — see the
// PR description for `chore/env-profile-consolidation`). The wrapper
// script now only accepts `sandbox` / `production`. Raw `next dev` or
// `vitest` without the wrapper resolves to "unknown" here — and since
// §8.428 an unknown profile resolves profiled vars to "" instead of
// reading the unsuffixed name (see resolveProfiledEnv).
//
// Note: the logger's `LogEnv` ("local" | "sandbox" | "production") is
// a separate vocabulary and still uses "local" to mean
// "developer machine, not a deployed env" for log-level + pretty-print
// defaults. See `shared/lib/logger/default.ts`.
import { resolveBeds24AuthMode } from "./beds24-auth-mode";

type RuntimeProfile = "sandbox" | "production" | "unknown";

const normalizeEnvValue = (value: string | undefined): string => value?.trim().toLowerCase() ?? "";

const resolveRuntimeProfile = (): RuntimeProfile => {
  const normalizedAppEnv = normalizeEnvValue(process.env.APP_ENV);
  if (normalizedAppEnv === "production" || normalizedAppEnv === "prod") {
    return "production";
  }

  if (normalizedAppEnv === "sandbox") {
    return "sandbox";
  }

  const normalizedNodeEnv = normalizeEnvValue(process.env.NODE_ENV);
  if (normalizedNodeEnv === "production") {
    return "production";
  }

  return "unknown";
};

const resolveProfiledEnv = (baseName: string, profile: RuntimeProfile): string => {
  // §8.428 — an UNKNOWN profile never reads the unsuffixed name. A
  // process that didn't choose a profile (bare `next dev`, a `tsx`
  // script run without scripts/run-with-runtime-env.sh) used to pick up
  // whatever plain DATABASE_URL / RESEND_API_KEY happened to be
  // exported in the shell — including a stale `source .env.production`.
  // Now it degrades to "" (which every consumer treats as
  // not-configured) and the fix is to run through the wrapper. Deployed
  // apps always have APP_ENV set, so they never hit this branch.
  if (profile === "unknown") {
    return "";
  }

  // The unsuffixed fallback is for DEPLOYED apps: each Fly app holds
  // exactly one environment's values, so its secrets use plain names.
  // Suffixes exist for the developer machine, where both environments'
  // values coexist in .env.sandbox / .env.production — those files keep
  // every profiled var suffixed (enforced by env-file-parity.test.ts),
  // so a dotenv-loaded file from the OTHER profile can never satisfy
  // this lookup.
  const profileSuffix = profile === "production" ? "PRODUCTION" : "SANDBOX";
  return process.env[`${baseName}_${profileSuffix}`] ?? process.env[baseName] ?? "";
};

const runtimeProfile = resolveRuntimeProfile();
const supabasePublishableKey =
  process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY ??
  process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_DEFAULT_KEY ??
  "";

const resolveBoolean = (raw: string | undefined): boolean =>
  normalizeEnvValue(raw) === "true";

export const env = {
  appEnv: process.env.APP_ENV ?? "",
  runtimeProfile,
  databaseUrl: resolveProfiledEnv("DATABASE_URL", runtimeProfile),
  directUrl: resolveProfiledEnv("DIRECT_URL", runtimeProfile),
  supabaseUrl: process.env.NEXT_PUBLIC_SUPABASE_URL ?? "",
  supabasePublishableKey,
  // Server-only admin key. Use Supabase's new opaque `sb_secret_…`
  // flavor (rotatable per-key, not a JWT). The legacy
  // `SUPABASE_SERVICE_ROLE_KEY` JWT fallback was removed 2026-05-19
  // after verifying every environment had `SUPABASE_SECRET_KEY` set
  // (Fly `replyless-dev` secrets + local `.env.<profile>` files).
  // If you're spinning up a new Supabase project, generate the
  // secret key under Dashboard → Settings → API → "New API Keys".
  supabaseSecretKey: process.env.SUPABASE_SECRET_KEY ?? "",
  // Auth (per docs/architecture/auth-tenancy.md §10 + 0.A4/0.A5)
  authAllowlistEnabled: resolveBoolean(process.env.AUTH_ALLOWLIST_ENABLED),
  // Public site origin used to compose OAuth redirectTo and webhook URLs
  // (per docs/operations/environment-profiles.md).
  siteUrl: process.env.NEXT_PUBLIC_SITE_URL ?? "",
  // OpenAI — used by the D-track AI draft adapter. The key is
  // server-only (never bundled to client); it is read at adapter
  // construction time and the adapter throws a friendly error if
  // missing. Model defaults to gpt-4o per DECISIONS §19.
  openaiApiKey: process.env.OPENAI_API_KEY ?? "",
  openaiModel: process.env.OPENAI_MODEL ?? "",
  // Separate model knob for embeddings (D1.2.b). Default
  // text-embedding-3-small per DECISIONS §19.2 — 1536-dim, cheap
  // ($0.02/M tokens), multilingual. The pgvector column is
  // vector(1536), so swapping to text-embedding-3-large (3072-dim)
  // would require a schema migration; keep this env var aligned
  // with the column dimension.
  openaiEmbeddingModel: process.env.OPENAI_EMBEDDING_MODEL ?? "",
  // Anthropic — alternative draft generator provider with stronger
  // tone-instruction following on short-fact answers (2026-06-03 live
  // tests confirmed gpt-4o ignored every prompt tone directive while
  // Claude haiku followed them on the first try). Read by the
  // AnthropicDraftGeneratorAdapter; toggle via `draftLlmProvider`.
  anthropicApiKey: process.env.ANTHROPIC_API_KEY ?? "",
  anthropicModel: process.env.ANTHROPIC_MODEL ?? "",
  // §8.628 — the host-text naturalness review pass only. UNSET keeps the
  // measured default (`HOST_TEXT_REVIEW_MODEL` in
  // `features/ai-draft/lib/reviewer-models.ts`); set it to roll that pass
  // back onto `anthropicModel`, or forward onto something else, without
  // touching the content pass — that one carries the KB and the whole
  // context, so it is the expensive one and stays on `anthropicModel`.
  draftReviewHostTextModel: process.env.DRAFT_REVIEW_HOST_TEXT_MODEL ?? "",
  // §8.677 — the send-text review pass (parity + naturalness on the
  // guest-language body). Same contract: UNSET keeps the measured
  // default (`SEND_TEXT_REVIEW_MODEL`), set to roll back or forward
  // independently of the other passes.
  draftReviewSendTextModel: process.env.DRAFT_REVIEW_SEND_TEXT_MODEL ?? "",
  // §8.665 — the KB 「AI で初期入力」 extractor. UNSET keeps the measured
  // default (`DEFAULT_MODEL` in the extractor adapter, gpt-5.4); set it
  // to roll that one call back (gpt-4o) or forward without a deploy.
  kbFillExtractorModel: process.env.KB_FILL_EXTRACTOR_MODEL ?? "",
  // §8.667 — same shape for the learn-from-host-reply extractor
  // (`OpenAIKbExtractionAdapter`, gpt-5.4 by default).
  kbExtractionModel: process.env.KB_EXTRACTION_MODEL ?? "",
  // §8.668 — same shape for the qa_pair answer-shift judge
  // (`OpenAIAnswerShiftJudgeAdapter`, gpt-5.4 by default).
  answerShiftJudgeModel: process.env.ANSWER_SHIFT_JUDGE_MODEL ?? "",
  // §8.679 — same shape for the write-time KB linter
  // (`OpenAIKbLintAdapter`, gpt-5.4 by default; mini was disqualified
  // by probe-8679 — nagging + corrupted suggestion text).
  kbLintModel: process.env.KB_LINT_MODEL ?? "",
  // §8.769 — same shape for the inbound reply judge that replaced the
  // burst-completeness + reply-necessity judges
  // (`OpenAIInboundReplyJudgeAdapter`, gpt-5.6-luna by default).
  inboundReplyJudgeModel: process.env.INBOUND_REPLY_JUDGE_MODEL ?? "",
  // Which provider the draft generator composition root wires up.
  // Values: "openai" (default, original) or "anthropic". Allows env-
  // only rollback if Anthropic has an outage / cost surprise.
  draftLlmProvider: (process.env.DRAFT_LLM_PROVIDER ?? "openai") as
    | "openai"
    | "anthropic",
  // §8.315 — shadow-model comparison. UNSET = OFF, and off means not a
  // single extra LLM call: the composition root only builds the shadow
  // adapter when this holds a model id. Set it (e.g. `gpt-5`) to have
  // every generated draft re-run against that candidate and stored for
  // side-by-side reading on /operator/shadow.
  //
  // Turning it on roughly DOUBLES the wall-clock of a generate_draft
  // tick (the candidate runs after the draft is persisted, ~15-25s), so
  // lower `maxJobsPerRun` if a tick starts running long.
  draftShadowModel: process.env.DRAFT_SHADOW_MODEL ?? "",
  // Reasoning effort for the candidate. Default `low` — measured at the
  // same 15-24s band as the live path's budgeted thinking, where the
  // provider default straddled the 90s cap. See the adapter.
  draftShadowReasoningEffort: process.env.DRAFT_SHADOW_REASONING_EFFORT ?? "",
  // §8.583 — the draft review pass. "off" (default) wires nothing: not a
  // single extra LLM call. "enforce" wraps the generator so every reply
  // draft is read by a second model against its sources, revised once
  // when it objects, and routed to a human when the objection survives
  // (see with-draft-review.ts). Anthropic-only (the reviewer adapter);
  // under DRAFT_LLM_PROVIDER=openai without an Anthropic key it stays off
  // and logs once. Flip per environment after the §8.583 replay
  // measurement, never by default in code.
  draftReviewMode: (process.env.DRAFT_REVIEW_MODE ?? "off") as
    | "off"
    | "enforce",
  // §8.609 — Anthropic `effort` (low / medium / high / xhigh / max) for
  // the drafter and the reviewer, on adaptive-thinking models (Sonnet
  // 5+), ON THE TWO HOST-DIRECTED ROUTES ONLY: composer 「AIに作成して
  // もらう」 (generate-stream) and 「AI に修正を依頼」/「AIに指示して作成」
  // (revise-stream) — the places a host sits and waits. The worker's
  // automatic draft and the KB page's 「AIをテスト」 (which must show
  // what the automatic draft would do) never read these — there is no
  // code path for them to. Unset = the API default. Measured on the
  // probe-8583 control set (§8.609): reviewer `medium` = the default's
  // verdict set at half the time; `low` missed an R3. Set per
  // environment, never by default in code.
  draftInteractiveGenerationEffort:
    process.env.DRAFT_INTERACTIVE_GENERATION_EFFORT ?? "",
  draftInteractiveReviewEffort:
    process.env.DRAFT_INTERACTIVE_REVIEW_EFFORT ?? "",
  // §8.770 / §8.810 — the retry attempt's effort. The first attempt is
  // fixed at medium; unset retry = `medium`, `off` = API default for the
  // retry only. See `resolveRetryGenerationEffort`.
  draftRetryGenerationEffort:
    process.env.DRAFT_RETRY_GENERATION_EFFORT ?? "",
  // Anthropic prompt caching for draft generation. §8.168 put the cache
  // breakpoint on the SHARED static prefix only (host-agnostic rules,
  // byte-identical for every host under one API key), so all draft
  // traffic reads one entry.
  // §8.325 — default ON everywhere, overridable. §8.168 defaulted this
  // OFF outside production on the premise that "sparse dev/sandbox
  // traffic can't amortize the write premium". Two things have since
  // made that false: the TTL moved from 5 minutes to 1 HOUR, and
  // `dev` is now the host's real operating console rather than a
  // scratch environment.
  //
  // Measured on 205 real drafts (2026-07-24): a preceding draft fell
  // inside the SAME 1h window 46.8% of the time — and break-even is
  // 1.28 calls per window (write 1.25x vs read 0.1x), so at that rate
  // caching runs ~29% cheaper on the cacheable slice. At the old 5-min
  // TTL the same data gives 14.6%, which really is a ~8% LOSS — the
  // original default was right for the world it was written in.
  //
  // Explicit `false` still wins, for a fast rollback if the live cache
  // metrics disagree with that projection.
  promptCacheEnabled: (process.env.PROMPT_CACHE_ENABLED ?? "true") === "true",
  // E2 send pipeline — which `Beds24MessageDeliveryPort` adapter
  // wires at composition root. `dryrun` (default, safe) writes a
  // synthetic `dryrun:<uuid>` external_message_id and performs no
  // network I/O. `http` POSTs to Beds24 v2 per swagger. Flip to
  // `http` at production launch via Fly secret. The codebase does
  // not have a hard fallback — leaving this unset lands on
  // `dryrun` so accidental missing secret can never send real
  // messages.
  beds24DeliveryMode: (process.env.BEDS24_DELIVERY_MODE ?? "dryrun") as
    | "dryrun"
    | "http",
  // Beds24 auth mode (§8.141) — decoupled from APP_ENV so a deployed
  // dev/sandbox server can run the real InviteCode + Vault flow without
  // pretending to be production. `invite_code` = onboarding exchanges an
  // invite code → per-org Vault token; `long_life` = the env-wide
  // LongLife dev shortcut. Unset → derived from APP_ENV (production →
  // invite_code, else long_life), so existing deploys are unchanged.
  // See `beds24-auth-mode.ts`.
  beds24AuthMode: resolveBeds24AuthMode(
    process.env.BEDS24_AUTH_MODE,
    process.env.APP_ENV,
  ),
  // Shared secret for internal cron-triggered routes (G1). The
  // scheduler (pg_cron + pg_net, G1.b) sends it as a Bearer token; the
  // route compares it constant-time. Server-only, never bundled.
  cronSecret: process.env.CRON_SECRET ?? "",
  // §8.438 — token for the EXTERNAL health probe (GAS uptime watcher)
  // hitting GET /api/health/cron. Separate from CRON_SECRET on purpose:
  // that one AUTHORIZES work (drains run under it) and lives only
  // between pg_net and the app; this one merely READS a health verdict
  // and is stored in a Google Apps Script property — different blast
  // radius, different secret. Unset → the route 404s (fail-closed).
  healthProbeToken: process.env.HEALTH_PROBE_TOKEN ?? "",
  // §8.228 — Stripe billing (DECISIONS §22). Built key-swappable BEFORE
  // the Stripe account existed: all three set → billing is live; any
  // missing → the billing page renders the honest 準備中 state, the
  // checkout/portal actions refuse with a typed error, the webhook
  // route 503s, and the quantity-sync drain no-ops. Profiled so sandbox
  // carries the test-mode key and production the live key.
  // Setup runbook: docs/operations/stripe-setup.md.
  stripeSecretKey: resolveProfiledEnv("STRIPE_SECRET_KEY", runtimeProfile),
  stripeWebhookSecret: resolveProfiledEnv(
    "STRIPE_WEBHOOK_SECRET",
    runtimeProfile,
  ),
  // The ONE volume-tiered JPY Price (§22 tiers baked into the Price's
  // tier table on the Stripe side; quantity = SUM(unit_count)).
  stripePriceId: resolveProfiledEnv("STRIPE_PRICE_ID", runtimeProfile),
  // §22.4 — card-less local trial length (landing promises 14 日間無料 /
  // クレジットカードの登録は不要). Computed from onboarded_at; this knob
  // only changes the window for NEW judgments, no schema involved.
  billingTrialDays: Number(process.env.BILLING_TRIAL_DAYS ?? "14"),
  // Beds24 v2 REST API base URL. Override only for sandbox replays /
  // test doubles; defaults to the live host. No trailing slash.
  // §8.199 — LOCAL enqueue blocklist (refines the §8.198 kill switch,
  // which blocked ALL local enqueues and made fixture-org dryrun E2E
  // needlessly ceremonial). Comma-separated org ids this process
  // REFUSES to create send jobs for. Set ONLY in a developer's local
  // env, listing the REAL org: the sandbox jobs queue is shared with
  // the deployed dev server (http + allow-all — the pre-launch
  // real-operation console), so a locally-enqueued job on a real
  // conversation would reach a REAL guest within one cron tick.
  // Fixture orgs are not listed → local dryrun E2E (enqueue → local
  // drain → synthetic ack) just works. Deployed envs never set this.
  sendEnqueueBlockedOrgIds: process.env.SEND_ENQUEUE_BLOCKED_ORG_IDS ?? "",
  // §8.224 — comma-separated emails allowed into the /operator/* pages
  // (beta invite management). Fail-closed: unset/empty = the pages 404
  // for EVERYONE. Compared against the session email, normalized
  // (trim + lowercase) on both sides.
  operatorEmails: process.env.OPERATOR_EMAILS ?? "",
  // §8.394 — Resend, for the billing notification emails. Profiled
  // like the Stripe keys so sandbox and production hold different
  // senders; the bare name is the fallback.
  resendApiKey: resolveProfiledEnv("RESEND_API_KEY", runtimeProfile),
  // §8.198 — send-allowlist interlock for the http delivery adapter.
  // unset/empty = refuse ALL outbound sends (fail-closed); "*" = allow
  // all (post-launch prod); "123,456" = only those Beds24 booking ids
  // (dev: the host's own test booking). Parsed by
  // `isBookingSendAllowed` (send-message/lib/send-allowlist.ts).
  beds24SendAllowedBookingIds:
    process.env.BEDS24_SEND_ALLOWED_BOOKING_IDS ?? "",
  beds24ApiBaseUrl: process.env.BEDS24_API_BASE_URL ?? "https://api.beds24.com/v2",
  // Beds24 v2 Long Life Token — used as the `token:` header on every
  // request. Dev workflow stores a read-only token per host (one host
  // per env). Pre-MVP-launch we migrate to the InviteCode refresh-flow
  // (refresh token persisted per-org in DB, short-life access token
  // cached). See memory `project_beds24_auth_strategy.md`.
  beds24LonglifeToken: process.env.BEDS24_LONGLIFE_TOKEN ?? "",
  // §8.80 (security hardening) — owner org id authorized to receive
  // the env-wide long-life token. When set, the `EnvLongLifeTokenProvider`
  // refuses any `organizationId` that doesn't match, so a rogue org
  // (e.g. someone who bypasses the allowlist) can never piggy-back on
  // the developer's Beds24 account. Empty / unset disables the gate
  // (legacy behaviour: any org gets the token — only acceptable for
  // local single-tenant test runs).
  beds24LonglifeOwnerOrgId:
    process.env.BEDS24_LONGLIFE_OWNER_ORG_ID ?? "",
  // §8.287 (B3) — Web Push VAPID pair. All three must be set for push
  // to work; the settings page and the send adapter treat missing
  // values as "push not configured" (fail-soft, no throw at import).
  // The public key ALSO ships to the client via NEXT_PUBLIC_ (read
  // directly in client code — this accessor is for server use).
  vapidPublicKey: process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY ?? "",
  vapidPrivateKey: process.env.VAPID_PRIVATE_KEY ?? "",
  vapidSubject: process.env.VAPID_SUBJECT ?? "",
};

// TODO: Add runtime validation for required env vars.
